import "./Yearly.css"
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import APIURL from "../../common/Config";
import Header from "../header/header";
import Footer from "../footer/footer";

const PiscesCard = () => {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inMemoryCacheRef = useRef(new Map());
  const [activeIndex, setActiveIndex] = useState(null);

  // Static metadata for PISCES
  const staticMeta = {
    title: "AstroBhagya – Free Pisces Yearly Horoscope by Best Astrologer",
    description: "Looking for accurate Pisces yearly horoscope predictions 2026? Discover free Pisces astrology predictions and annual insights by zodiac sign for a better year ahead.",
    keywords: "Free Pisces yearly horoscope predictions 2026, Pisces yearly Astrology predictions, Pisces annual predictions, Yearly horoscope by Pisces zodiac sign",
    author: "Heavenly Expectation Private Limited",
  };

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const fetchPiscesYearly = useCallback(async (retries = 3) => {
    const dateStr = "20260101"; // 2026 data
    const cacheKey = `pisces_yearly_${dateStr}`; 
    setError(null);
    setLoading(true);

    // 1. CHECK LOCALSTORAGE
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setHoroscopeData(parsed);
        setLoading(false);
        return;
      } catch (e) {
        console.error("Failed to parse cached data:", e);
      }
    }

    // 2. CHECK IN-MEMORY CACHE
    if (inMemoryCacheRef.current.has(cacheKey)) {
      setHoroscopeData(inMemoryCacheRef.current.get(cacheKey));
      setLoading(false);
      return;
    }

    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn('Fetch request timed out after 15 seconds.');
      }, 15000);

      const response = await fetch(
        `${APIURL}/horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // Find PISCES data (id: 41 or sunShine contains "PISCES") 
        const piscesData = data.find(
          item => item.id === 41 || item.sunShine?.toUpperCase().includes("PISCES")
        );

        if (piscesData) {
          // Cache the data
          inMemoryCacheRef.current.set(cacheKey, piscesData);
          localStorage.setItem(cacheKey, JSON.stringify(piscesData));
          setHoroscopeData(piscesData);
        } else {
          throw new Error("Pisces data not found in API response");
        }
      } else {
        throw new Error("Unexpected response format from API");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The network request timed out. Please check your internet connection and try again.');
        console.error("Network timeout error:", error);
      } else {
        setError(`Failed to load yearly horoscope: ${error.message}. Please try again.`);
        console.error("Error fetching Pisces yearly data:", error);
      }

      if (retries > 0) {
        console.log(`Retrying fetch for Pisces Yearly. Retries left: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchPiscesYearly(retries - 1);
      } else {
        // FALLBACK DATA - PISCES 2026 🔥
        console.log("All retries exhausted. Using fallback data.");
        const fallbackData = {
          sunShine: "Pisces 2026: Your year, as the stars align",
          prediction: `<div class="horoscope">
            <p>2026 encourages Pisces inner exploration and personal growth. Intuition guides emotional clarity and creative breakthroughs.</p>
            <h2>Love and Relationships</h2>
            <p>Emotional depth strengthens bonds. Authentic connections built on empathy and trust.</p>
            <h2>Career and Finances</h2>
            <p>Creative opportunities reward intuition. Strategic financial planning ensures stability.</p>
            <h2>Health & Wellness</h2>
            <p>Holistic routines nurture body/mind. Meditation maintains emotional harmony.</p>
            <h2>Astrological Remedies</h2>
            <ul>
              <li><strong>Moonstone/Aquamarine:</strong> Enhances intuition and calm.</li>
              <li><strong>Spiritual Practices:</strong> Meditation deepens inner connection.</li>
              <li><strong>Saturday Charity:</strong> White items alleviate obstacles.</li>
            </ul>
          </div>`
        };
        setHoroscopeData(fallbackData);
      }
    } finally {
      await minLoadingTime;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPiscesYearly();
  }, [fetchPiscesYearly]);

  // Set meta tags
  useEffect(() => {
    document.title = staticMeta.title;

    const setOrCreateMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute("content", content);
      } else {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        metaTag.setAttribute("content", content);
        document.head.appendChild(metaTag);
      }
    };

    setOrCreateMetaTag("description", staticMeta.description);
    setOrCreateMetaTag("keywords", staticMeta.keywords);
    setOrCreateMetaTag("author", staticMeta.author);
    setOrCreateMetaTag("revisit-after", "7 days");
    setOrCreateMetaTag("distribution", "global");
    setOrCreateMetaTag("rating", "general");

    let charsetMeta = document.querySelector("meta[charset]");
    if (!charsetMeta) {
      charsetMeta = document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      document.head.prepend(charsetMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading Pisces 2026 Horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => fetchPiscesYearly()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div>
        <section id="Make">
          <div className="year-container">
            <h4 className="mt-3 mb-3 text-center font-weight-bold">
              {horoscopeData?.sunShine || "Pisces 2026: Your year, as the stars align"}
            </h4>
            <div className="icons-wrapper text-center mb-4">
              <img
                src={require("../../assets/Piscesss.webp")}
                alt="Pisces"
                className="horoscope-icon mx-2"
              />
            </div>

            {horoscopeData?.prediction && (
              <main>
                <div
                  dangerouslySetInnerHTML={{
                    __html: horoscopeData.prediction
                  }}
                />
              </main>
            )}

            <div className="btn-wrapper">
              <Link to="/yearly-horoscope/aquarius" className="btn btn-customs">
                <i className="bi bi-arrow-left"></i> Aquarius Horoscope 2026
              </Link>
              <Link to="/yearly-horoscope/aries" className="btn btn-customs">
                <i className="bi bi-arrow-right"></i> Aries Horoscope 2026
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ SECTION - SAME AS PREVIOUS */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Pisces Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What are the core personality traits of Pisces?",
                  answer: "Pisces individuals are known for their empathy, intuition, creativity, and spiritual depth. Ruled by Neptune/Jupiter, they excel in compassion and imagination.",
                },
                {
                  question: "2. What can Pisces expect in love and relationships in 2026?",
                  answer: "2026 brings emotional depth and stronger bonds. Empathy and open communication create meaningful connections.",
                },
                {
                  question: "3. What does 2026 look like for Pisces career growth?",
                  answer: "Creative opportunities reward intuition. Professional growth through dedication and strategic planning.",
                },
                {
                  question: "4. What health advice for Pisces in 2026?",
                  answer: "Holistic routines nurture body/mind. Meditation, yoga maintain emotional harmony.",
                },
                {
                  question: "5. Key astrological remedies for Pisces?",
                  answer: "Moonstone/Aquamarine, spiritual practices, and Saturday charity enhance clarity.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PiscesCard;
